import {
  useConversationAgentReplyMode,
  useConversationParticipants,
} from "@app/hooks/conversations";
import { useURLSheet } from "@app/hooks/useURLSheet";
import { useAppRouter } from "@app/lib/platform";
import { setQueryParam } from "@app/lib/utils/router";
import { agentAvatarVisual } from "@app/types/assistant/avatar";
import type {
  AgentReplyMode,
  ConversationMetadata,
} from "@app/types/assistant/conversation";
import { getConversationAgentReplyMode } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  ChevronDown,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
  Sidekick,
} from "@dust-tt/sparkle";
import { useMemo } from "react";

const maxStackedAvatars = 4;

interface ConversationHeaderControlsProps {
  owner: LightWorkspaceType;
  conversationId: string;
  metadata: ConversationMetadata | null | undefined;
}

// Who is in the conversation and how agents answer, up front in the header rather than in
// the "..." menu.
export function ConversationHeaderControls({
  owner,
  conversationId,
  metadata,
}: ConversationHeaderControlsProps) {
  return (
    <>
      <AgentReplyModeButton
        owner={owner}
        conversationId={conversationId}
        metadata={metadata}
      />
      <ParticipantsStack owner={owner} conversationId={conversationId} />
    </>
  );
}

interface ParticipantsStackProps {
  owner: LightWorkspaceType;
  conversationId: string;
}

function ParticipantsStack({ owner, conversationId }: ParticipantsStackProps) {
  const router = useAppRouter();
  const { onOpenChange: onOpenChangeAgentModal } = useURLSheet("agentDetails");
  const { onOpenChange: onOpenChangeUserModal } = useURLSheet("userDetails");
  const { conversationParticipants } = useConversationParticipants({
    conversationId,
    workspaceId: owner.sId,
  });

  const participants = useMemo(
    () => [
      ...(conversationParticipants?.agents ?? []).map((agent) => ({
        key: `agent-${agent.configurationId}`,
        name: agent.name,
        isAgent: true,
        visual: agentAvatarVisual({
          sId: agent.configurationId,
          name: agent.name,
          pictureUrl: agent.pictureUrl,
        }),
        onSelect: () => {
          onOpenChangeAgentModal(true);
          setQueryParam(router, "agentDetails", agent.configurationId);
        },
      })),
      ...(conversationParticipants?.users ?? []).map((user) => ({
        key: `user-${user.sId}`,
        name: user.fullName ?? user.username,
        isAgent: false,
        visual: user.pictureUrl,
        onSelect: () => {
          onOpenChangeUserModal(true);
          setQueryParam(router, "userDetails", user.sId);
        },
      })),
    ],
    [
      conversationParticipants,
      onOpenChangeAgentModal,
      onOpenChangeUserModal,
      router,
    ]
  );

  if (participants.length === 0) {
    return null;
  }

  const stacked = participants.slice(0, maxStackedAvatars);
  const hiddenCount = participants.length - stacked.length;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${participants.length} in this chat: ${participants
            .map((participant) => participant.name)
            .join(", ")}`}
          className="flex h-9 min-w-11 shrink-0 items-center rounded-full px-1.5 hover:bg-muted-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-muted-background-night"
        >
          <span className="flex items-center -space-x-2">
            {stacked.map((participant) => (
              <Avatar
                key={participant.key}
                size="xs"
                visual={participant.visual}
                name={participant.name}
                isRounded
                className="ring-2 ring-app-background dark:ring-app-background-night"
              />
            ))}
            {hiddenCount > 0 && (
              <span className="z-10 flex h-6 min-w-6 items-center justify-center rounded-full bg-muted-background px-1 text-xs font-semibold text-foreground ring-2 ring-app-background dark:bg-muted-background-night dark:text-foreground-night dark:ring-app-background-night">
                +{hiddenCount}
              </span>
            )}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel label={`In this chat (${participants.length})`} />
        {participants.map((participant) => (
          <DropdownMenuItem
            key={participant.key}
            label={participant.name}
            description={participant.isAgent ? "Agent" : "Person"}
            onClick={participant.onSelect}
            icon={
              <Avatar
                size="xs"
                visual={participant.visual}
                name={participant.name}
                isRounded
              />
            }
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface AgentReplyModeButtonProps {
  owner: LightWorkspaceType;
  conversationId: string;
  metadata: ConversationMetadata | null | undefined;
}

function AgentReplyModeButton({
  owner,
  conversationId,
  metadata,
}: AgentReplyModeButtonProps) {
  const { isUpdatingAgentReplyMode, updateAgentReplyMode } =
    useConversationAgentReplyMode({ owner, conversationId });
  const agentReplyMode = getConversationAgentReplyMode(metadata);
  const current =
    agentReplyModeOptions.find((option) => option.mode === agentReplyMode) ??
    agentReplyModeOptions[0];

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Agents reply: ${current.label}`}
          disabled={isUpdatingAgentReplyMode}
          className={cn(
            "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-medium",
            "transition-colors duration-150 ease-out disabled:cursor-wait disabled:opacity-60",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            agentReplyMode === "auto"
              ? "border-highlight-300 bg-highlight-50 text-highlight-700 hover:bg-highlight-100"
              : "border-border bg-muted-background text-foreground hover:bg-background dark:border-border-night dark:bg-muted-background-night dark:text-foreground-night"
          )}
        >
          <Icon visual={Sidekick} size="xs" />
          {current.label}
          <Icon visual={ChevronDown} size="xs" className="opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel label="When should agents reply?" />
        <DropdownMenuRadioGroup value={agentReplyMode}>
          {agentReplyModeOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option.mode}
              value={option.mode}
              label={option.label}
              description={option.description}
              onClick={() => {
                if (option.mode !== agentReplyMode) {
                  void updateAgentReplyMode(option.mode);
                }
              }}
            />
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const agentReplyModeOptions: {
  mode: AgentReplyMode;
  label: string;
  description: string;
}[] = [
  {
    mode: "auto",
    label: "Auto reply",
    description: "Agents answer messages on their own",
  },
  {
    mode: "tagged",
    label: "@mention only",
    description: "Agents stay quiet until someone @mentions them",
  },
];
